import { Component, Input, ElementRef, ViewChild, AfterViewInit, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as echarts from 'echarts';

@Component({
  selector: 'app-sparkline-chart',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="relative w-[140px] h-[50px] rounded-lg overflow-hidden border border-white/[0.1] bg-white/[0.05] flex-shrink-0" style="display: block; min-width: 140px;">
      <div #chartContainer class="w-full h-full relative z-0"></div>
    </div>
  `
})
export class SparklineChartComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() data: any[] = [];
  @Input() inRange: boolean = true;
  
  @ViewChild('chartContainer') chartContainer!: ElementRef;
  private chartInstance: echarts.ECharts | null = null;

  ngAfterViewInit() {
    this.initChart();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.chartInstance) {
      this.updateChart();
    }
  }

  ngOnDestroy() {
    if (this.chartInstance) {
      this.chartInstance.dispose();
    }
    window.removeEventListener('resize', this.onResize);
  }

  private initChart() {
    setTimeout(() => {
      if (!this.chartContainer?.nativeElement) return;
      if (this.chartInstance) {
        this.chartInstance.dispose();
      }
      this.chartInstance = echarts.init(this.chartContainer.nativeElement);
      this.updateChart();
      
      window.addEventListener('resize', this.onResize);
    }, 100);
  }

  private onResize = () => {
    if (this.chartInstance) {
      this.chartInstance.resize();
    }
  };

  private updateChart() {
    if (!this.chartInstance || !this.data || !this.data.length) return;

    console.log('[SparklineChart] Raw Geckoterminal OHLCV data:', this.data);

    // Extract close prices from OHLCV data [timestamp, open, high, low, close, volume]
    let prices = this.data.map(item => {
      if (Array.isArray(item) && item.length >= 5) {
        return Number(item[4]); // close price
      }
      return Number(item) || 0;
    }).filter(p => p > 0 && !isNaN(p));

    console.log('[SparklineChart] Extracted valid close prices:', prices);

    if (prices.length === 0) return;

    // Optional: clamp extreme outliers to prevent crazy spikes from bad Geckoterminal API wicks
    if (prices.length > 5) {
        const sorted = [...prices].sort((a,b) => a - b);
        const q1 = sorted[Math.floor((sorted.length / 4))];
        const q3 = sorted[Math.floor((sorted.length * (3 / 4)))];
        const iqr = q3 - q1;
        const lowerBound = q1 - 1.5 * iqr;
        const upperBound = q3 + 1.5 * iqr;
        
        prices = prices.map(p => {
           if (p < lowerBound) return lowerBound;
           if (p > upperBound) return upperBound;
           return p;
        });
        
        console.log('[SparklineChart] Prices after clamping extreme outliers (IQR filter):', prices);
    }

    const currentPrice = prices[prices.length - 1];
    const color = this.inRange ? '#22c55e' : '#a1a1aa'; // Green or Gray
    const colorRgba = this.inRange ? '34, 197, 94' : '161, 161, 170';

    const minP = Math.min(...prices);
    const maxP = Math.max(...prices);
    const range = maxP - minP;
    const padding = range < (minP * 0.001) ? minP * 0.01 : range * 0.15;

    const option = {
      animation: false,
      grid: { left: 0, right: 0, top: 4, bottom: 4 },
      xAxis: {
        type: 'category',
        show: false,
        boundaryGap: false
      },
      yAxis: {
        type: 'value',
        show: false,
        min: minP - padding,
        max: maxP + padding
      },
      series: [
        {
          data: prices,
          type: 'line',
          showSymbol: false,
          smooth: 0.25, // Smoother line
          lineStyle: {
            color: color,
            width: 1.5
          },
          itemStyle: {
            color: color
          },
          markLine: {
            symbol: 'none',
            silent: true,
            label: { show: false },
            lineStyle: {
                color: color,
                width: 1,
                type: 'dashed',
                opacity: 0.5
            },
            data: [
                { yAxis: currentPrice }
            ]
          },
          markPoint: {
            symbol: 'circle',
            symbolSize: 4,
            itemStyle: { color: color },
            data: [
                { 
                  coord: [prices.length - 1, currentPrice] 
                }
            ]
          }
        }
      ]
    };

    this.chartInstance.setOption(option, true);
  }
}
